// ============================================
// P3 支付线（P-08 前端）：套餐与权益卡片（个人中心）
// - 展示当前套餐/能力位（/auth/quota）
// - 「升级 / 续费套餐」→ 套餐列表 → 支付弹窗（支付宝/微信）
// ============================================
import { useCallback, useEffect, useState } from 'react';
import { Card, Space, Typography, Button, Tag, Modal, Table, message, Alert } from 'antd';
import { CrownOutlined } from '@ant-design/icons';
import client from '../../api/client';
import { fetchPayPackages, fetchPayStatus, fetchPayGrants, type PayPackage } from '../../api/pay';
import PayDialog from './PayDialog';
import dayjs from 'dayjs';

const { Text } = Typography;

interface QuotaInfo {
  packageCode: string;
  editSessionLimit: number;
  viewSessionLimit: number;
  subAccountSeats: number;
  subAccountUsed?: number | null;
  watermark?: boolean;
  publish3d?: boolean;
  paidMaterials?: boolean;
}

const PACKAGE_LABEL: Record<string, string> = {
  free: '免费版',
  basic: '基础版',
  pro: '专业版',
  enterprise: '尊享版',
  flagship: '旗舰版',
};

export default function PlanCard() {
  const [quota, setQuota] = useState<QuotaInfo | null>(null);
  const [packages, setPackages] = useState<PayPackage[]>([]);
  const [mock, setMock] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [expireAt, setExpireAt] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [selected, setSelected] = useState<PayPackage | null>(null);

  const loadQuota = useCallback(async () => {
    try {
      const res = await client.get('/auth/quota');
      setQuota(res.data);
    } catch {
      setQuota(null);
    }
  }, []);

  /** 订阅有效期（grants.expireAt；无则为长期） */
  const loadExpire = useCallback(async () => {
    try {
      const data = await fetchPayGrants({ productType: 'subscription' });
      const exp = (data.items || [])
        .map((g) => g.expireAt)
        .filter((v): v is string => !!v)
        .sort()
        .pop();
      setExpireAt(exp ?? null);
    } catch {
      setExpireAt(null);
    }
  }, []);

  useEffect(() => {
    void loadQuota();
    void loadExpire();
    fetchPayStatus()
      .then((s) => { setEnabled(s.enabled); setMock(s.mock); })
      .catch(() => setEnabled(false));
  }, [loadQuota, loadExpire]);

  const openPicker = async () => {
    try {
      const [pkgs] = await Promise.all([fetchPayPackages(), loadQuota()]);
      setPackages(pkgs.items || []);
      setPickerOpen(true);
    } catch (e: any) {
      message.error(e?.response?.data?.message || '套餐列表获取失败');
    }
  };

  const choose = (pkg: PayPackage) => {
    setSelected(pkg);
    setPickerOpen(false);
    setPayOpen(true);
  };

  return (
    <Card
      style={{ marginBottom: 16 }}
      data-testid="plan-card"
      title={<span><CrownOutlined style={{ color: '#faad14', marginRight: 6 }} />套餐与权益</span>}
      extra={enabled ? (
        <Button type="primary" size="small" onClick={openPicker} data-testid="plan-upgrade-btn">升级 / 续费套餐</Button>
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>线上支付未启用</Text>
      )}
    >
      {quota ? (
        <Space orientation="vertical" size={4}>
          <Space>
            <Text strong>当前套餐：</Text>
            <Tag color={quota.packageCode === 'free' ? 'default' : 'gold'} data-testid="plan-code">
              {PACKAGE_LABEL[quota.packageCode] || quota.packageCode}
            </Tag>
            {quota.paidMaterials && <Tag color="blue">付费素材</Tag>}
            {quota.publish3d && <Tag color="purple">3D 发布</Tag>}
            {quota.watermark === false && <Tag color="green">无水印</Tag>}
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            编辑会话 {quota.editSessionLimit} · 预览会话 {quota.viewSessionLimit} · 子账号 {quota.subAccountUsed ?? '-'}/{quota.subAccountSeats}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }} data-testid="plan-expire">
            有效期：{expireAt ? `至 ${dayjs(expireAt).format('YYYY-MM-DD HH:mm')}` : '长期有效'}
          </Text>
        </Space>
      ) : (
        <Text type="secondary">套餐信息加载失败（UBS 未连接时显示为空）</Text>
      )}

      <Modal
        open={pickerOpen}
        onCancel={() => setPickerOpen(false)}
        footer={null}
        width={640}
        title="选择套餐"
      >
        {mock && <Alert type="info" showIcon style={{ marginBottom: 12 }} message="当前为 Mock 支付模式（本地联调）：下单后可点「模拟支付成功」" />}
        <Table<PayPackage>
          rowKey="id"
          size="small"
          dataSource={packages}
          pagination={false}
          data-testid="plan-package-table"
          columns={[
            { title: '套餐', dataIndex: 'name', render: (v, r) => <Space>{v}{quota?.packageCode === r.id ? <Tag color="gold">当前</Tag> : null}</Space> },
            { title: '价格', dataIndex: 'price', width: 120, render: (v) => <Text strong>¥{Number(v).toFixed(2)}</Text> },
            { title: '有效期', dataIndex: 'validMonth', width: 100, render: (v) => (v > 0 ? `${v} 个月` : '长期') },
            {
              title: '操作', width: 100,
              render: (_, r) => <Button size="small" type="primary" onClick={() => choose(r)} data-testid={`plan-choose-${r.id}`}>购买</Button>,
            },
          ]}
        />
      </Modal>

      <PayDialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        mock={mock}
        orderInput={selected ? {
          orderType: 'subscription',
          packageId: selected.id,
          subject: `套餐订阅-${selected.name}`,
        } : null}
        onSuccess={() => {
          void loadQuota();
          message.success('套餐已生效');
        }}
      />
    </Card>
  );
}
